import React from "react";
import { Card, Typography, Space, Dropdown } from "antd";
import type { MenuProps } from "antd";
import {
  ShareAltOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import {
  FolderIcon,
  ArchiveBoxIcon,
  DocumentTextIcon,
  PhotoIcon,
  VideoCameraIcon,
  MusicalNoteIcon,
  CodeBracketIcon,
  CircleStackIcon,
  LockClosedIcon,
  StarIcon,
  HeartIcon,
  UserGroupIcon,
} from "@heroicons/react/24/solid";
import { VaultFolder } from "@/types";
import { ShareBadge } from "@/components/shared";

const { Text } = Typography;

const iconMap: Record<string, React.ComponentType<React.SVGProps<SVGSVGElement>>> = {
  folder: FolderIcon,
  "folder-open": ArchiveBoxIcon,
  file: DocumentTextIcon,
  picture: PhotoIcon,
  video: VideoCameraIcon,
  sound: MusicalNoteIcon,
  code: CodeBracketIcon,
  database: CircleStackIcon,
  lock: LockClosedIcon,
  star: StarIcon,
  heart: HeartIcon,
  team: UserGroupIcon,
};

interface FolderCardProps {
  folder: VaultFolder;
  isSelected?: boolean;
  onClick?: (e: React.MouseEvent) => void;
  onDoubleClick?: () => void;
  onContextMenu?: (e: React.MouseEvent) => void;
  onShare?: () => void;
  onRename?: () => void;
  onDelete?: () => void;
  canShare?: boolean;
}

export default function FolderCard({
  folder,
  isSelected = false,
  onClick,
  onDoubleClick,
  onContextMenu,
  onShare,
  onRename,
  onDelete,
  canShare = true,
}: FolderCardProps) {
  const isShared = folder.sharedWith?.length > 0;
  const IconComponent = iconMap[folder.icon] || FolderIcon;

  const shareMenuItem: MenuProps["items"] = canShare
    ? [
        {
          key: "share",
          label: "Share folder",
          icon: <ShareAltOutlined />,
          onClick: (e) => {
            e.domEvent.stopPropagation();
            onShare?.();
          },
        },
      ]
    : [];

  const menuItems: MenuProps["items"] = [
    ...shareMenuItem,
    {
      key: "rename",
      label: "Rename",
      icon: <EditOutlined />,
      onClick: (e) => {
        e.domEvent.stopPropagation();
        onRename?.();
      },
    },
    { type: "divider" },
    {
      key: "delete",
      label: "Delete",
      icon: <DeleteOutlined />,
      danger: true,
      onClick: (e) => {
        e.domEvent.stopPropagation();
        onDelete?.();
      },
    },
  ];

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    onContextMenu?.(e);
  };

  return (
    <Dropdown menu={{ items: menuItems }} trigger={["contextMenu"]}>
      <Card
        className={`folder-card transition-all ${isSelected ? "selected-item" : ""}`}
        hoverable
        onClick={onClick}
        onDoubleClick={onDoubleClick}
        onContextMenu={handleContextMenu}
        style={{
          cursor: "pointer",
          borderRadius: 8,
          border: isSelected ? "1px solid #0F6E56" : "1px solid rgba(0, 0, 0, 0.08)",
        }}
        styles={{
          body: {
            padding: 16,
          },
        }}
      >
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
          <IconComponent
            style={{
              width: 32,
              height: 32,
              color: folder.color || "#0F6E56",
            }}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <Text
              strong
              ellipsis
              style={{
                display: "block",
                fontSize: 14,
                marginBottom: 4,
              }}
              title={folder.name}
            >
              {folder.name}
            </Text>
            <Space size={8}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {folder.itemCount} {folder.itemCount === 1 ? "item" : "items"}
              </Text>
              {isShared && <ShareBadge />}
            </Space>
          </div>
        </div>
      </Card>
    </Dropdown>
  );
}
